'use client';
/**
 * Dashboard — эталонная реализация дизайн-системы.
 *
 * Здесь собраны все паттерны, на которых поедут остальные экраны: KPI,
 * карта, таблица, статусы, алерты, фильтры, кнопки, три состояния данных
 * и поведение на 1366 / 1920.
 *
 * Принципы, которые проверяются именно тут:
 *  · плотность выше «дизайнерской», но контролируемая — диспетчер держит
 *    экран открытым восемь часов, ему нужны строки, а не воздух;
 *  · оранжевый только на действии, статусы — семантика (зелёный «едет»,
 *    синий «запланирован», янтарный «загрузка», красный «просрочка»);
 *  · каждая панель грузится и падает независимо: сломанный /fuel не должен
 *    уносить весь экран;
 *  · цифры моноширинные и выровнены по разрядам.
 *
 * Данные берутся из тех же эндпойнтов, что и раньше — бизнес-логика,
 * права и контракты API не менялись.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import dynamic from 'next/dynamic';
import Link from 'next/link';
import {
  Plus, RefreshCw, Truck, Users,
  ArrowRight, Map as MapIcon, Inbox,
} from 'lucide-react';
import { api } from '@/lib/api';
import { useT } from '@/lib/useT';
import { useAuthStore } from '@/store/auth.store';
import { fmtEur } from '@/lib/format-eur';
import { Button, Badge, Panel, PanelHeader, StatusDot, cx, type Tone } from '@/components/ui';
import {
  PageHeader, KpiCard, KpiRow, AlertList, EmptyState, ErrorState,
  EntityRow, FilterPills, type AlertItem,
} from '@/components/patterns';
import { DataTable, type Column } from '@/components/patterns/DataTable';

const DriverDashboard = dynamic(() => import('@/components/dashboard/DriverDashboard'), { ssr: false });
// Карта только на клиенте: leaflet трогает window на импорте.
const OperationsMap = dynamic(() => import('@/components/dashboard/OperationsMap'), {
  ssr: false,
  loading: () => <div className="h-[360px] bg-[var(--surface-sunken)] animate-pulse" />,
});

/* ── Типы ответов API ───────────────────────────────────────────── */

interface AdminStats { users: number; vehicles: number; fuelTodayLiters: number; leavePending: number }

type OrderStatus = 'NEW' | 'ASSIGNED' | 'IN_TRANSIT' | 'DELIVERED' | 'CANCELLED' | string;

interface Order {
  id: string;
  publicId: string;
  customerName: string;
  pickupAddress: string;
  deliveryAddress: string;
  status: OrderStatus;
  scheduledAt?: string | null;
  deliveredAt?: string | null;
  priceEur?: number | null;
  assignedUser?: { name?: string; surname?: string } | null;
  assignedVehicle?: { plateNumber?: string } | null;
}

interface DriverStatus {
  userId: string;
  name?: string;
  surname?: string;
  status?: string;          // DRIVING / RESTING / OFF …
  vehiclePlate?: string | null;
  drivingMinutes?: number | null;
}

interface Vehicle { id: string; plateNumber: string; status?: string | null; model?: string | null }

/* ── Статусы заказа → семантика ─────────────────────────────────── */

const ORDER_TONE: Record<string, Tone> = {
  IN_TRANSIT: 'success',
  DELIVERED: 'success',
  ASSIGNED: 'info',
  NEW: 'neutral',
  LOADING: 'warning',
  CANCELLED: 'danger',
};

const ORDER_LABEL: Record<string, string> = {
  NEW: 'New', ASSIGNED: 'Assigned', IN_TRANSIT: 'In transit',
  DELIVERED: 'Delivered', CANCELLED: 'Cancelled', LOADING: 'Loading',
};

type OrderFilter = 'all' | 'NEW' | 'ASSIGNED' | 'IN_TRANSIT' | 'DELIVERED';

export default function DashboardPage() {
  const t = useT();
  const { user } = useAuthStore();
  const isDriver = user?.role === 'DRIVER' || user?.orgRole === 'DRIVER';

  const [stats, setStats] = useState<AdminStats | null>(null);
  const [orders, setOrders] = useState<Order[]>([]);
  const [drivers, setDrivers] = useState<DriverStatus[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<OrderFilter>('all');

  /* ── Загрузка: каждая панель переживает падение соседней ─────── */
  const load = useCallback(async (soft = false) => {
    soft ? setRefreshing(true) : setLoading(true);
    setError(null);
    const [s, o, d, v] = await Promise.allSettled([
      api.get('/admin/stats') as Promise<AdminStats>,
      api.get('/orders') as Promise<Order[]>,
      api.get('/time/drivers/all') as Promise<DriverStatus[]>,
      api.get('/vehicles') as Promise<Vehicle[]>,
    ]);
    if (s.status === 'fulfilled') setStats(s.value);
    if (o.status === 'fulfilled') setOrders(Array.isArray(o.value) ? o.value : []);
    if (d.status === 'fulfilled') setDrivers(Array.isArray(d.value) ? d.value : []);
    if (v.status === 'fulfilled') setVehicles(Array.isArray(v.value) ? v.value : []);
    // Экран считается сломанным, только если не пришло ничего.
    if ([s, o, d, v].every((r) => r.status === 'rejected')) {
      setError('Could not load dashboard data');
    }
    soft ? setRefreshing(false) : setLoading(false);
    // Без зависимостей: `t` меняет идентичность на каждый рендер, и если
    // положить его сюда, useEffect ниже перезапускается бесконечно —
    // дашборд уходил в цикл запросов и ловил 429 от throttler'а.
  }, []);

  useEffect(() => {
    if (isDriver) return;
    load();
    // load стабилен (пустые deps), поэтому эффект отрабатывает один раз.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDriver]);

  /* ── Производные метрики ────────────────────────────────────── */
  const m = useMemo(() => {
    const by = (s: string) => orders.filter((o) => o.status === s).length;
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const deliveredToday = orders.filter(
      (o) => o.status === 'DELIVERED' && o.deliveredAt && new Date(o.deliveredAt) >= today,
    ).length;
    const revenue = orders
      .filter((o) => o.status !== 'CANCELLED')
      .reduce((sum, o) => sum + (o.priceEur ?? 0), 0);
    const driving = drivers.filter((d) => (d.status ?? '').toUpperCase() === 'DRIVING').length;
    const activeVehicles = vehicles.filter((v) => (v.status ?? '').toUpperCase() === 'ACTIVE').length;
    const utilisation = vehicles.length ? Math.round((driving / vehicles.length) * 100) : 0;
    return {
      active: orders.filter((o) => o.status !== 'DELIVERED' && o.status !== 'CANCELLED').length,
      inTransit: by('IN_TRANSIT'),
      deliveredToday,
      revenue,
      driving,
      activeVehicles,
      utilisation,
      newOrders: by('NEW'),
    };
  }, [orders, drivers, vehicles]);

  const filtered = useMemo(
    () => (filter === 'all' ? orders : orders.filter((o) => o.status === filter)).slice(0, 12),
    [orders, filter],
  );

  const alerts: AlertItem[] = useMemo(() => {
    const out: AlertItem[] = [];
    if (stats?.leavePending) {
      out.push({ id: 'leave', tone: 'info', title: 'Leave requests waiting for approval',
                 meta: `${stats.leavePending}`, href: '/dashboard/leave' });
    }
    const unassigned = orders.filter((o) => o.status === 'NEW').length;
    if (unassigned) {
      out.push({ id: 'unassigned', tone: 'warning', title: 'Orders without a driver',
                 meta: `${unassigned}`, href: '/dashboard/orders' });
    }
    const maintenance = vehicles.filter((v) => (v.status ?? '').toUpperCase() === 'MAINTENANCE').length;
    if (maintenance) {
      out.push({ id: 'maint', tone: 'warning', title: 'Vehicles in maintenance',
                 meta: `${maintenance}`, href: '/dashboard/fleet/timeline' });
    }
    return out;
  }, [stats, orders, vehicles]);

  /* Водитель видит свой дашборд — другой сценарий, не режем его под диспетчера. */
  if (isDriver) return <DriverDashboard />;

  const columns: Column<Order>[] = [
    {
      // publicId — это cuid; целиком он в таблице только шумит, поэтому
      // показываем хвост, а полный номер отдаём в title и по клику.
      key: 'id', header: 'Order', width: '104px',
      sortValue: (o) => o.publicId,
      cell: (o) => (
        <Link
          href={`/dashboard/orders?id=${o.id}`}
          title={o.publicId}
          className="font-mono text-[12px] text-[var(--text-primary)] hover:text-[var(--brand)]"
        >
          #{(o.publicId || o.id).slice(-6).toUpperCase()}
        </Link>
      ),
    },
    {
      key: 'customer', header: 'Customer',
      sortValue: (o) => o.customerName,
      cell: (o) => (
        <span className="block max-w-[220px] truncate text-[var(--text-primary)]" title={o.customerName}>
          {o.customerName}
        </span>
      ),
    },
    {
      key: 'route', header: 'Route',
      cell: (o) => (
        <span className="inline-flex items-center gap-1.5 text-[12.5px]">
          <span className="truncate max-w-[150px]">{o.pickupAddress}</span>
          <ArrowRight size={11} className="text-[var(--text-muted)] shrink-0" />
          <span className="truncate max-w-[150px]">{o.deliveryAddress}</span>
        </span>
      ),
    },
    {
      key: 'driver', header: 'Driver', hideBelow: true,
      sortValue: (o) => o.assignedUser?.name ?? '',
      cell: (o) => o.assignedUser
        ? (
          <span className="leading-tight">
            <span className="block text-[12.5px]">{[o.assignedUser.name, o.assignedUser.surname].filter(Boolean).join(' ')}</span>
            {o.assignedVehicle?.plateNumber && (
              <span className="block font-mono text-[11px] text-[var(--text-muted)]">{o.assignedVehicle.plateNumber}</span>
            )}
          </span>
        )
        : <span className="text-[var(--text-muted)]">—</span>,
    },
    {
      key: 'status', header: 'Status', width: '118px',
      sortValue: (o) => o.status,
      cell: (o) => (
        <Badge tone={ORDER_TONE[o.status] ?? 'neutral'} dot>
          {ORDER_LABEL[o.status] ?? o.status}
        </Badge>
      ),
    },
    {
      key: 'price', header: 'Price', align: 'right', numeric: true, width: '96px', hideBelow: true,
      sortValue: (o) => o.priceEur ?? 0,
      cell: (o) => (o.priceEur != null
        ? <span className="text-[var(--text-primary)]">{fmtEur(o.priceEur)}</span>
        : <span className="text-[var(--text-muted)]">—</span>),
    },
  ];

  const label = (key: string, fallback: string) => {
    const v = t(key);
    return !v || v === key ? fallback : v;
  };

  if (error && !stats && !orders.length) {
    return <ErrorState message={error} onRetry={() => load()} />;
  }

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title={label('nav_dashboard', 'Dashboard')}
        subtitle={new Date().toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'long' })}
        actions={
          <>
            <Button size="md" icon={RefreshCw} onClick={() => load(true)} disabled={refreshing}>
              {refreshing ? 'Updating…' : 'Refresh'}
            </Button>
            {/* Прямого создания заказа в org-роли нет: они приходят из портала
                клиента, маркетплейса, импорта и шаблонов. Ведём в шаблоны. */}
            <Button size="md" variant="primary" icon={Plus} onClick={() => { window.location.href = '/dashboard/orders/templates'; }}>
              {label('ord_from_template', 'New from template')}
            </Button>
          </>
        }
      />

      {/* ── KPI: шесть чисел, которыми диспетчер меряет день ───── */}
      <KpiRow>
        <KpiCard label="Active orders" value={loading ? '—' : m.active} hint={`${m.newOrders} unassigned`} />
        <KpiCard label="In transit" value={loading ? '—' : m.inTransit} />
        <KpiCard label="Delivered today" value={loading ? '—' : m.deliveredToday} />
        <KpiCard label="Order value" value={loading ? '—' : fmtEur(m.revenue)} hint="open orders" />
        <KpiCard label="Fuel today" value={loading ? '—' : Math.round(stats?.fuelTodayLiters ?? 0)} unit="L" />
        <KpiCard label="Fleet in motion" value={loading ? '—' : `${m.utilisation}%`} hint={`${m.driving} of ${vehicles.length} vehicles`} />
      </KpiRow>

      {/* ── Что происходит сейчас: карта + что требует внимания ── */}
      <div className="grid gap-5 grid-cols-1 xl:grid-cols-[minmax(0,1.7fr)_minmax(320px,1fr)]">
        <Panel className="overflow-hidden">
          <PanelHeader
            title="Live map"
            meta={`${m.driving} moving`}
            action={<Link href="/dashboard/map"><Button size="sm" icon={MapIcon}>Open map</Button></Link>}
          />
          <OperationsMap height={360} />
        </Panel>

        <div className="flex flex-col gap-5 min-w-0">
          <Panel>
            <PanelHeader
              title="Needs attention"
              meta={alerts.length ? `${alerts.length}` : undefined}
              action={<Link href="/dashboard/incidents"><Button size="sm" variant="ghost">All</Button></Link>}
            />
            <AlertList items={alerts} empty="Everything is on track" />
          </Panel>

          <Panel className="flex-1">
            <PanelHeader
              title="Fleet status"
              meta={`${vehicles.length} vehicles`}
              action={<Link href="/dashboard/fleet"><Button size="sm" variant="ghost">Fleet</Button></Link>}
            />
            {loading ? (
              <div className="p-4 flex flex-col gap-2.5">
                {[...Array(3)].map((_, i) => (
                  <span key={i} className="h-3.5 rounded-[var(--radius-xs)] bg-[var(--surface-hover)] animate-pulse" style={{ width: `${70 - i * 12}%` }} />
                ))}
              </div>
            ) : vehicles.length === 0 ? (
              <EmptyState icon={Truck} title="No vehicles" hint="Add a truck to see its status here." />
            ) : (
              <div className="grid grid-cols-2 divide-x divide-y divide-[var(--border)] border-t border-[var(--border)]">
                <FleetStat label="Driving" value={m.driving} tone="success" />
                <FleetStat label="Active" value={m.activeVehicles} tone="info" />
                <FleetStat label="Maintenance" value={vehicles.filter((v) => (v.status ?? '').toUpperCase() === 'MAINTENANCE').length} tone="warning" />
                <FleetStat label="Idle" value={Math.max(vehicles.length - m.driving - vehicles.filter((v) => (v.status ?? '').toUpperCase() === 'MAINTENANCE').length, 0)} tone="neutral" />
              </div>
            )}
          </Panel>
        </div>
      </div>

      {/* ── Что в работе: заказы ────────────────────────────────── */}
      <Panel className="overflow-hidden">
        <PanelHeader
          title={label('nav_orders', 'Orders')}
          meta={`${filtered.length} of ${orders.length}`}
          action={<Link href="/dashboard/orders"><Button size="sm">View all</Button></Link>}
        />
        <div className="px-4 py-3 border-b border-[var(--border)]">
          <FilterPills<OrderFilter>
            value={filter}
            onChange={setFilter}
            options={[
              { key: 'all', label: 'All', count: orders.length },
              { key: 'NEW', label: 'New', count: orders.filter((o) => o.status === 'NEW').length },
              { key: 'ASSIGNED', label: 'Assigned', count: orders.filter((o) => o.status === 'ASSIGNED').length },
              { key: 'IN_TRANSIT', label: 'In transit', count: m.inTransit },
              { key: 'DELIVERED', label: 'Delivered', count: orders.filter((o) => o.status === 'DELIVERED').length },
            ]}
          />
        </div>
        <DataTable
          columns={columns}
          rows={filtered}
          keyOf={(o) => o.id}
          loading={loading}
          empty={
            <EmptyState
              icon={Inbox}
              title={filter === 'all' ? 'No orders yet' : 'No orders with this status'}
              hint={filter === 'all' ? 'Create the first order or import a list from Excel.' : undefined}
              action={filter === 'all'
                ? <Link href="/dashboard/orders?new=1"><Button size="sm" variant="primary" icon={Plus}>New order</Button></Link>
                : <Button size="sm" onClick={() => setFilter('all')}>Show all</Button>}
            />
          }
        />
      </Panel>

      {/* ── Кто в смене: вспомогательный блок ───────────────────── */}
      <Panel className="overflow-hidden">
        <PanelHeader
          title="Drivers on shift"
          meta={`${drivers.length}`}
          action={<Link href="/dashboard/drivers"><Button size="sm" variant="ghost">All drivers</Button></Link>}
        />
        {drivers.length === 0 ? (
          <EmptyState icon={Users} title="Nobody on shift" hint="Driver statuses appear once a shift starts." />
        ) : (
          <ul className="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3">
            {drivers.slice(0, 6).map((d) => {
              const st = (d.status ?? '').toUpperCase();
              const tone: Tone = st === 'DRIVING' ? 'success' : st === 'RESTING' ? 'warning' : 'neutral';
              return (
                <EntityRow
                  key={d.userId}
                  title={[d.name, d.surname].filter(Boolean).join(' ') || '—'}
                  meta={d.vehiclePlate ?? undefined}
                  badge={<Badge tone={tone} dot>{st ? st[0] + st.slice(1).toLowerCase() : 'Off'}</Badge>}
                  href="/dashboard/drivers"
                />
              );
            })}
          </ul>
        )}
      </Panel>
    </div>
  );
}

/* ── Мелкий блок статуса автопарка ──────────────────────────────── */

function FleetStat({ label, value, tone }: { label: string; value: number; tone: Tone }) {
  return (
    <div className="flex flex-col gap-1 p-3">
      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
        <StatusDot tone={tone} /> {label}
      </span>
      <b className={cx('text-[19px] leading-none font-semibold tabular-nums text-[var(--text-primary)]')}>{value}</b>
    </div>
  );
}
